import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import client from "../api/client";
import { logEmployeeActivity } from "../utils/activityLogger";
import "./Tasks.css";

const STATUS_OPTIONS = [
  "To Do",
  "Assigned",
  "In Progress",
  "Follow Up",
  "Review",
  "On Hold",
  "Completed",
];
const STATUS_TABS = [
  "All",
  "Pending",
  "Assigned",
  "In Progress",
  "Follow Up",
  "Review",
  "On Hold",
  "Completed",
  "Overdue",
];
const DRAWER_TABS = [
  "Overview",
  "Timeline",
  "Notes",
  "Followups",
  "Attachments",
  "Reviews",
  "Employee Feedback",
];
const CHART_COLORS = ["#2563eb", "#16a34a", "#f59e0b", "#dc2626", "#7c3aed", "#0891b2"];
const PRIORITY_OPTIONS = ["Urgent", "High", "Medium", "Low"];
const ASSIGNMENT_OPTIONS = [
  { value: "tl", label: "TL Based" },
  { value: "direct", label: "Direct Employee" },
];

const EMPTY_FORM = {
  title: "",
  description: "",
  assignmentType: "direct",
  tlId: "",
  employeeIds: [],
  category: "",
  priority: "Medium",
  deadline: "",
  taskType: "",
  status: "To Do",
  progress: 0,
};

const parseStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const normalizeRole = (value) => String(value || "").trim().toLowerCase();

const ADMIN_ROLES = new Set([
  "admin",
  "subadmin",
  "superadmin",
  "super_admin",
  "administrator",
]);

const getEffectiveRole = (user) => {
  const roleCandidates = [
    user?.role,
    user?.role_name,
    user?.user_role,
    user?.user_type,
    user?.account_type,
  ];

  return roleCandidates.map(normalizeRole).find(Boolean) || "";
};

const getAssignedIds = (task) => {
  if (Array.isArray(task?.assigned_employee_ids)) {
    return task.assigned_employee_ids
      .map((value) => Number(value))
      .filter(Number.isFinite);
  }

  if (typeof task?.assigned_employee_ids === "string") {
    return task.assigned_employee_ids
      .split(",")
      .map((value) => Number(value))
      .filter(Number.isFinite);
  }

  return [];
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatFollowupTime = (value) => {
  if (!value) return "";

  const rawValue = String(value).trim();
  const match = rawValue.match(/^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/);
  if (!match) return "";

  const [, hourValue, minuteValue] = match;
  const hour = Number(hourValue);
  const minute = Number(minuteValue);

  if (
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return "";
  }

  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${String(displayHour).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${period}`;
};

const formatFollowupDateTime = (dateValue, timeValue) => {
  if (!dateValue) return { date: "-", time: formatFollowupTime(timeValue) };

  const rawValue = String(dateValue).trim();
  const match = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (!match) return { date: formatDateOnly(dateValue), time: formatFollowupTime(timeValue) };

  const [, year, month, day] = match;
  const date = `${day}/${month}/${year}`;
  const time = formatFollowupTime(timeValue);

  return { date, time };
};

const getPriorityClass = (priority) =>
  `priority-badge ${(priority || "low").toLowerCase()}`;

const getStatusClass = (status) =>
  `status-chip ${String(status || "To Do").toLowerCase().replace(/\s+/g, "-")}`;

const normalizeStatus = (status) => {
  const value = String(status || "").trim().toLowerCase();
  if (!value) return "Pending";
  if (["to do", "todo", "pending", "open", "new"].includes(value)) return "Pending";
  if (value.includes("assign")) return "Assigned";
  if (value.includes("progress") || value.includes("working")) return "In Progress";
  if (value.includes("follow")) return "Follow Up";
  if (value.includes("review")) return "Review";
  if (value.includes("hold") || value.includes("pause")) return "On Hold";
  if (value.includes("complete") || value.includes("done") || value.includes("closed")) return "Completed";
  return status || "Pending";
};

const isCompletedTask = (task) => normalizeStatus(task?.status) === "Completed";

const isTaskOverdue = (task) => {
  if (!task?.deadline || isCompletedTask(task)) return false;
  const deadline = new Date(task.deadline);
  if (Number.isNaN(deadline.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  deadline.setHours(0, 0, 0, 0);
  return deadline < today;
};

const isTodayDate = (value) => {
  if (!value) return false;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;
  const today = new Date();
  return parsed.toDateString() === today.toDateString();
};

const toDateKey = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const getTimelineLabel = (log) => {
  const action = String(
    log?.action_type || log?.action || log?.flow_type || log?.event_type || ""
  ).toLowerCase();
  if (action.includes("reassign")) return "Reassigned";
  if (action.includes("note")) return "Note Added";
  if (action.includes("review")) return "Review Added";
  if (action.includes("follow")) return "Followup Added";
  if (action.includes("attachment")) return "Attachment Uploaded";
  if (action.includes("complete")) return "Completed";
  if (action.includes("assign")) return "Assigned";
  if (action.includes("create")) return "Task Created";
  return log?.action || log?.action_type || "Task Update";
};

const getSuggestionLabel = (suggestion) =>
  suggestion?.title ||
  suggestion?.suggestion ||
  suggestion?.name ||
  suggestion?.description ||
  `Suggestion #${suggestion?.id || ""}`;

const getSuggestionDescription = (suggestion) =>
  suggestion?.description || suggestion?.remarks || suggestion?.summary || "";

const toCategoryKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const SUMMARY_CARD_ICON_CLASS = {
  blue: "fa fa-tasks",
  amber: "fa fa-users",
  green: "fa fa-check-circle",
  red: "fa fa-exclamation-triangle",
  orange: "fa fa-bar-chart",
  slate: "fa fa-line-chart",
  purple: "fa fa-paperclip",
};

export default function Tasks() {
  const navigate = useNavigate();
  const currentUser = useMemo(() => parseStoredUser(), []);
  const currentUserId = Number(currentUser?.id) || null;
  const currentRole = getEffectiveRole(currentUser);
  const isAdminOrSubadmin = ADMIN_ROLES.has(currentRole);
 const canAssign = true;
  const canReassign = isAdminOrSubadmin || currentRole === "tl";
 const canManageCategories = true;
  const isTl = currentRole === "tl";
  const isEmployee = currentRole === "employee";

  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categorySaving, setCategorySaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectedTaskNotes, setSelectedTaskNotes] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState("Overview");
  const [taskFlowLogs, setTaskFlowLogs] = useState([]);
  const [taskReviews, setTaskReviews] = useState([]);
  const [taskFeedback, setTaskFeedback] = useState([]);
  const [taskFollowups, setTaskFollowups] = useState([]);
  const [taskAttachments, setTaskAttachments] = useState([]);
  const [todayFollowups, setTodayFollowups] = useState([]);
const [todayAttachments, setTodayAttachments] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reassignEmployeeIds, setReassignEmployeeIds] = useState([]);
  const [reassignReason, setReassignReason] = useState("");
  const [noteText, setNoteText] = useState("");
  const [followupDate, setFollowupDate] = useState("");
  const [followupRemark, setFollowupRemark] = useState("");
  const [followupSaving, setFollowupSaving] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [attachmentSaving, setAttachmentSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [categoriesError, setCategoriesError] = useState("");
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categoryModalError, setCategoryModalError] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    priority: "",
    assignmentType: "",
    employee: "",
    statusTab: "All",
    viewMode: "tasks",
  });

  const tlUsers = useMemo(
    () =>
      employees.filter(
        (employee) => String(employee?.role || "").toLowerCase() === "tl"
      ),
    [employees]
  );

  const staffUsers = useMemo(() => {
    const filtered = employees.filter((employee) => {
      const role = String(employee?.role || "").toLowerCase();
      return role === "employee";
    });

    if (filtered.length) {
      return filtered;
    }

    return employees.filter((employee) => {
      const role = String(employee?.role || "").toLowerCase();
      return !["admin", "subadmin"].includes(role);
    });
  }, [employees]);

  const tlOptions = useMemo(
    () => tlUsers.map((employee) => ({ value: employee.id, label: employee.name })),
    [tlUsers]
  );

  const employeeOptions = useMemo(
    () =>
      staffUsers.map((employee) => ({
        value: employee.id,
        label: employee.name,
      })),
    [staffUsers]
  );

  const categoryOptions = useMemo(() => {
    const optionMap = new Map();
    const appendCategory = (value) => {
      const name = String(value || "").trim();
      if (!name) {
        return;
      }

      const key = toCategoryKey(name);
      if (!optionMap.has(key)) {
        optionMap.set(key, { value: name, label: name });
      }
    };

    categories.forEach((category) => appendCategory(category?.name || category));
    tasks.forEach((task) => appendCategory(task?.category));
    appendCategory(form.category);

    return Array.from(optionMap.values()).sort((left, right) =>
      left.label.localeCompare(right.label)
    );
  }, [categories, form.category, tasks]);

  const selectedCategoryOption = useMemo(() => {
    const currentCategory = String(form.category || "").trim();
    if (!currentCategory) {
      return null;
    }

    return (
      categoryOptions.find(
        (option) => toCategoryKey(option.value) === toCategoryKey(currentCategory)
      ) || {
        value: currentCategory,
        label: currentCategory,
      }
    );
  }, [categoryOptions, form.category]);

  const selectedTask = useMemo(
    () => tasks.find((task) => Number(task.id) === Number(selectedTaskId)) || null,
    [tasks, selectedTaskId]
  );

  const fetchEmployees = async () => {
    const res = await client.get("/users");
    setEmployees(Array.isArray(res.data) ? res.data : []);
  };

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    setCategoriesError("");

    try {
      const res = await client.get("/task-categories");
      const rows = Array.isArray(res.data) ? res.data : [];
      setCategories(rows);
      return rows;
    } catch (err) {
      console.error("Fetch task categories error:", err);
      setCategories([]);
      setCategoriesError(
        "Failed to load live categories. Existing task categories are still available."
      );
      return [];
    } finally {
      setCategoriesLoading(false);
    }
  };

  const normalizeTaskPayload = (payload) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (!payload || typeof payload !== "object") {
      return [];
    }

    const directCandidates = [
      payload.tasks,
      payload.data,
      payload.rows,
      payload.results,
      payload.items,
    ];

    const directMatch = directCandidates.find(Array.isArray);
    if (directMatch) {
      return directMatch;
    }

    for (const value of Object.values(payload)) {
      const nested = normalizeTaskPayload(value);
      if (nested.length) {
        return nested;
      }
    }

    return [];
  };

  const fetchTasks = async () => {
    let rows = [];

    if (isTl && currentUserId) {
      try {
        const tlRes = await client.get(`/tasks/tl/${currentUserId}`);
        rows = normalizeTaskPayload(tlRes.data);
      } catch (error) {
        console.warn("TL task fetch fallback:", error?.message || error);
        const fallback = await client.get("/tasks");
        rows = normalizeTaskPayload(fallback.data).filter((task) => {
          const assignedIds = getAssignedIds(task);
          return (
            Number(task?.tl_id) === currentUserId ||
            Number(task?.assigned_to) === currentUserId ||
            assignedIds.includes(currentUserId)
          );
        });
      }
    } else {
      const res = await client.get("/tasks");
      rows = normalizeTaskPayload(res.data);
    }



    setTasks(rows);
  };

  const fetchTaskNotes = async (taskId) => {
    if (!taskId) {
      setSelectedTaskNotes([]);
      return;
    }

    setNotesLoading(true);
    try {
      const res = await client.get(`/tasks/${taskId}/notes`);
      setSelectedTaskNotes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch task notes error:", err);
      setSelectedTaskNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  const fetchTaskDetails = async (taskId) => {
    if (!taskId) {
      setTaskFlowLogs([]);
      setTaskReviews([]);
      setTaskFeedback([]);
      setTaskFollowups([]);
      setTaskAttachments([]);
      return;
    }

    setDetailLoading(true);
    try {
      const [flowRes, reviewRes, feedbackRes, followupRes, attachmentRes] =
        await Promise.allSettled([
          client.get(`/tasks/${taskId}/flow-logs`),
          client.get(`/tasks/${taskId}/reviews`),
          client.get(`/tasks/${taskId}/employee-feedback`),
          client.get(`/tasks/${taskId}/followups`),
          client.get(`/tasks/${taskId}/attachments`),
        ]);

      setTaskFlowLogs(
        flowRes.status === "fulfilled" && Array.isArray(flowRes.value.data)
          ? flowRes.value.data
          : []
      );
      setTaskReviews(
        reviewRes.status === "fulfilled" && Array.isArray(reviewRes.value.data)
          ? reviewRes.value.data
          : []
      );
      setTaskFeedback(
        feedbackRes.status === "fulfilled" && Array.isArray(feedbackRes.value.data)
          ? feedbackRes.value.data
          : []
      );
      setTaskFollowups(
        followupRes.status === "fulfilled" && Array.isArray(followupRes.value.data)
          ? followupRes.value.data
          : []
      );
      setTaskAttachments(
        attachmentRes.status === "fulfilled" && Array.isArray(attachmentRes.value.data)
          ? attachmentRes.value.data
          : []
      );
    } catch (err) {
      console.error("Fetch task detail drawer error:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchTodayFollowups = async () => {
    try {
      const res = await client.get("/tasks/today-followups");
      setTodayFollowups(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Today followups error:", err);
      setTodayFollowups([]);
    }
  };

  const fetchTodayAttachments = async () => {
    try {
      const res = await client.get("/tasks/today-attachments");
      setTodayAttachments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Today attachments error:", err);
      setTodayAttachments([]);
    }
  };

  const loadPageData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchEmployees(),
        fetchTasks(),
        fetchCategories(),
        fetchTodayFollowups(),
        fetchTodayAttachments()
      ]);
    } catch (err) {
      console.error("Task page load error:", err);
      alert("Failed to load task data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPageData();
  }, []);

  useEffect(() => {
    if (selectedTaskId) {
      fetchTaskNotes(selectedTaskId);
      fetchTaskDetails(selectedTaskId);
      return;
    }

    setSelectedTaskNotes([]);
    setTaskFlowLogs([]);
    setTaskReviews([]);
    setTaskFeedback([]);
    setTaskFollowups([]);
    setTaskAttachments([]);
  }, [selectedTaskId]);

  useEffect(() => {
    const fetchSuggestions = async () => {
      const category = form.category.trim();
      if (!category) {
        setSuggestions([]);
        return;
      }

      setSuggestionsLoading(true);
      try {
        const res = await client.get(
          `/task-suggestions/${encodeURIComponent(category)}`
        );
        setSuggestions(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.warn("Suggestion fetch skipped:", err?.message || err);
        setSuggestions([]);
      } finally {
        setSuggestionsLoading(false);
      }
    };

    fetchSuggestions();
  }, [form.category]);

  const resetWorkspace = () => {
    setForm({
      ...EMPTY_FORM,
      assignmentType: isTl ? "direct" : "direct",
      tlId: isTl && currentUserId ? String(currentUserId) : "",
    });
    setSelectedTaskId(null);
    setReassignEmployeeIds([]);
    setReassignReason("");
    setSelectedTaskNotes([]);
    setNoteText("");
    setFollowupDate("");
    setFollowupRemark("");
    setAttachmentFiles([]);
    setDrawerOpen(false);
    setDrawerTab("Overview");
  };

  useEffect(() => {
    resetWorkspace();
  }, [currentUserId, isTl]);

  const handleFieldChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "assignmentType" && value === "tl"
        ? { employeeIds: [] }
        : {}),
      ...(key === "assignmentType" && value === "direct"
        ? { tlId: isTl && currentUserId ? String(currentUserId) : "" }
        : {}),
    }));
  };

  const openCategoryModal = () => {
    if (!canManageCategories) {
      return;
    }

    setCategoryModalError("");
    setNewCategoryName("");
    setIsCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    if (categorySaving) {
      return;
    }

    setIsCategoryModalOpen(false);
    setNewCategoryName("");
    setCategoryModalError("");
  };

  const handleCreateCategory = async (event) => {
    event.preventDefault();

    const normalizedName = String(newCategoryName || "").trim();
    if (!normalizedName) {
      setCategoryModalError("Category name is required.");
      return;
    }

    setCategorySaving(true);
    setCategoryModalError("");
    setCategoriesError("");

    try {
      const res = await client.post("/task-categories", { name: normalizedName });
      const savedName = String(res.data?.category?.name || normalizedName).trim();

      await fetchCategories();
      handleFieldChange("category", savedName);
      setIsCategoryModalOpen(false);
      setNewCategoryName("");
      setCategoryModalError("");
    } catch (err) {
      if (err?.response?.status === 409) {
        const existingName = String(
          err.response?.data?.category?.name || normalizedName
        ).trim();

        await fetchCategories();
        handleFieldChange("category", existingName);
        setCategoriesError(
          "Category already exists. The existing category has been selected."
        );
        setIsCategoryModalOpen(false);
        setNewCategoryName("");
        setCategoryModalError("");
        return;
      }

      console.error("Create task category error:", err);
      setCategoryModalError(
        err?.response?.data?.message || "Failed to save category."
      );
    } finally {
      setCategorySaving(false);
    }
  };

  const filteredTasks = useMemo(() => {
    const search = filters.search.trim().toLowerCase();

    return tasks.filter((task) => {
      const normalizedStatus = normalizeStatus(task?.status);
      const activeTab = filters.statusTab || "All";
      const matchesSearch =
        !search ||
        [
          String(task?.title || "").toLowerCase(),
          String(task?.description || "").toLowerCase(),
          String(task?.assigned_employees || "").toLowerCase(),
          String(task?.category || "").toLowerCase(),
          String(task?.task_type || "").toLowerCase(),
        ].some((field) => field.includes(search));

      const matchesStatus =
        !filters.status || normalizeStatus(task?.status) === filters.status;
      const matchesStatusTab =
        activeTab === "All" ||
        (activeTab === "Overdue" && isTaskOverdue(task)) ||
        normalizedStatus === activeTab;
      const matchesPriority =
        !filters.priority || String(task?.priority || "") === filters.priority;
      const matchesAssignmentType =
        !filters.assignmentType ||
        String(task?.assignment_type || "") === filters.assignmentType;
      const matchesEmployee =
        !filters.employee ||
        [
          String(task?.assigned_employees || "").toLowerCase(),
          String(task?.tl_name || "").toLowerCase(),
        ].some((field) => field.includes(filters.employee.toLowerCase()));

      return (
        matchesSearch &&
        matchesStatus &&
        matchesStatusTab &&
        matchesPriority &&
        matchesAssignmentType &&
        matchesEmployee
      );
    });
  }, [filters, tasks]);

  const summaryCards = useMemo(() => {
    return [
      { label: "Total Tasks", value: tasks.length, tone: "blue", filter: "All" },
      {
        label: "Pending Tasks",
        value: tasks.filter((task) => normalizeStatus(task?.status) === "Pending").length,
        tone: "amber",
        filter: "Pending",
      },
      {
        label: "In Progress Tasks",
        value: tasks.filter((task) => normalizeStatus(task?.status) === "In Progress").length,
        tone: "orange",
        filter: "In Progress",
      },
      {
        label: "Completed Tasks",
        value: tasks.filter((task) => normalizeStatus(task?.status) === "Completed").length,
        tone: "green",
        filter: "Completed",
      },
      {
        label: "Overdue Tasks",
        value: tasks.filter(isTaskOverdue).length,
        tone: "red",
        filter: "Overdue",
      },
      {
        label: "Today's Followups",
        value: todayFollowups.length,
        tone: "slate",
        filter: "Follow Up",
      },
      {
        label: "Today's Attachments",
        value: todayAttachments.length,
        tone: "purple",
        filter: null,
      },
    ];
  }, [tasks, todayFollowups, todayAttachments]);

  const openTask = (task) => {
    setSelectedTaskId(task.id);
    setDrawerOpen(true);
    setDrawerTab("Overview");
    setForm({
      title: task?.title || "",
      description: task?.description || "",
      assignmentType: task?.assignment_type || "direct",
      tlId: task?.tl_id ? String(task.tl_id) : isTl && currentUserId ? String(currentUserId) : "",
      employeeIds: getAssignedIds(task),
      category: task?.category || "",
      priority: task?.priority || "Medium",
      deadline: task?.deadline
        ? new Date(task.deadline).toISOString().slice(0, 10)
        : "",
      taskType: task?.task_type || "",
      status: task?.status || "To Do",
      progress: Number(task?.progress) || 0,
    });
    setReassignEmployeeIds(getAssignedIds(task));
    setReassignReason(task?.reassign_reason || "");
    setNoteText("");
  };

  const getSelectedEmployeeActivityTargets = (employeeIds = []) =>
    employeeIds.map((employeeId) => {
      const matchedEmployee = employees.find(
        (employee) => Number(employee.id) === Number(employeeId)
      );

      return {
        employeeId,
        employeeName: matchedEmployee?.name || `Employee #${employeeId}`,
      };
    });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedTaskId && !canAssign) {
      alert("You do not have permission to assign tasks");
      return;
    }

    const normalizedEmployeeIds = form.employeeIds
      .map((id) => Number(id))
      .filter(Number.isFinite);
    const normalizedTlId = Number(form.tlId) || null;

    if (!form.title.trim()) {
      alert("Task title is required");
      return;
    }

    if (!selectedTaskId) {
      if (form.assignmentType === "tl" && !normalizedTlId) {
        alert("Please select a TL");
        return;
      }

      if (form.assignmentType === "direct" && normalizedEmployeeIds.length === 0) {
        alert("Please select at least one employee");
        return;
      }
    }

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      category: form.category.trim(),
      priority: form.priority,
      deadline: form.deadline || null,
      task_type: form.taskType.trim(),
      status: form.status,
      progress: Number(form.progress) || 0,
      assignment_type: form.assignmentType,
      tl_id:
        form.assignmentType === "tl"
          ? normalizedTlId
          : isTl && currentUserId
          ? currentUserId
          : null,
      employeeIds: form.assignmentType === "direct" ? normalizedEmployeeIds : [],
    };

    setSaving(true);
    try {
      if (selectedTaskId) {
        await client.put(`/tasks/${selectedTaskId}`, payload);
      } else {
        await client.post("/tasks", payload);
      }

      const activityTargets =
        payload.assignment_type === "direct"
          ? getSelectedEmployeeActivityTargets(payload.employeeIds)
          : payload.tl_id
          ? getSelectedEmployeeActivityTargets([payload.tl_id])
          : [];

      void Promise.allSettled(
        activityTargets.map((target) =>
          logEmployeeActivity({
            targetEmployeeId: target.employeeId,
            targetEmployeeName: target.employeeName,
            action: selectedTaskId ? "Task Updated" : "Task Assigned",
            module: "Tasks",
            description: `${
              selectedTaskId ? "Updated" : "Assigned"
            } task "${payload.title}" for ${target.employeeName}.`,
          })
        )
      );

      await fetchTasks();
      resetWorkspace();
    } catch (err) {
      console.error("Save task error:", err);
      alert(selectedTaskId ? "Failed to update task" : "Failed to assign task");
    } finally {
      setSaving(false);
    }
  };

  const handleReassign = async () => {
    if (!selectedTaskId) {
      alert("Select a task first");
      return;
    }

    if (!canReassign) {
      alert("You do not have permission to reassign tasks");
      return;
    }

    const normalizedEmployeeIds = reassignEmployeeIds
      .map((id) => Number(id))
      .filter(Number.isFinite);

    if (!normalizedEmployeeIds.length || !reassignReason.trim()) {
      alert("Employees and reassignment reason are required");
      return;
    }

    setReassigning(true);
    try {
      await client.put(`/tasks/${selectedTaskId}/reassign`, {
        new_assigned_to_ids: normalizedEmployeeIds,
        new_assigned_to: normalizedEmployeeIds[0],
        reassign_reason: reassignReason.trim(),
      });

      void Promise.allSettled(
        getSelectedEmployeeActivityTargets(normalizedEmployeeIds).map((target) =>
          logEmployeeActivity({
            targetEmployeeId: target.employeeId,
            targetEmployeeName: target.employeeName,
            action: "Task Reassigned",
            module: "Tasks",
            description: `Reassigned task "${selectedTask?.title || "Task"}" to ${target.employeeName}.`,
          })
        )
      );

      await fetchTasks();
      const refreshedTaskId = selectedTaskId;
      resetWorkspace();
      setSelectedTaskId(refreshedTaskId);
    } catch (err) {
      console.error("Reassign task error:", err);
      alert("Failed to reassign task");
    } finally {
      setReassigning(false);
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;

    try {
      await client.delete(`/tasks/${taskId}`);
      if (Number(selectedTaskId) === Number(taskId)) {
        resetWorkspace();
      }
      await fetchTasks();
    } catch (err) {
      console.error("Delete task error:", err);
      alert("Failed to delete task");
    }
  };

  const handleAddNote = async () => {
    if (!selectedTaskId || !noteText.trim()) {
      alert("Add a note before saving");
      return;
    }

    setNoteSaving(true);
    try {
      await client.post(`/tasks/${selectedTaskId}/notes`, {
        employee_id: currentUserId,
        employee_name: currentUser?.name || "Unknown User",
        note: noteText.trim(),
      });
      setNoteText("");
      await fetchTaskNotes(selectedTaskId);
      await fetchTaskDetails(selectedTaskId);
    } catch (err) {
      console.error("Add task note error:", err);
      alert("Failed to add note");
    } finally {
      setNoteSaving(false);
    }
  };

  const handleAddFollowup = async () => {
    if (!selectedTaskId || !followupDate || !followupRemark.trim()) {
      alert("Followup date and remark are required");
      return;
    }

    setFollowupSaving(true);
    try {
      await client.post(`/tasks/${selectedTaskId}/followups`, {
        followup_date: followupDate,
        remark: followupRemark.trim(),
        created_by: currentUserId,
        created_by_name: currentUser?.name || "Unknown User",
      });
      setFollowupDate("");
      setFollowupRemark("");
      await fetchTasks();
      await fetchTaskDetails(selectedTaskId);
    } catch (err) {
      console.error("Add task followup error:", err);
      alert(err?.response?.data?.message || "Failed to add followup");
    } finally {
      setFollowupSaving(false);
    }
  };

  const handleUploadAttachments = async () => {
    if (!selectedTaskId || !attachmentFiles.length) {
      alert("Choose at least one attachment");
      return;
    }

    const formData = new FormData();
    attachmentFiles.forEach((file) => formData.append("attachments", file));

    setAttachmentSaving(true);
    try {
      await client.post(`/tasks/${selectedTaskId}/attachments`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachmentFiles([]);
      await fetchTaskDetails(selectedTaskId);
    } catch (err) {
      console.error("Upload task attachments error:", err);
      alert(err?.response?.data?.message || "Failed to upload attachments");
    } finally {
      setAttachmentSaving(false);
    }
  };

  const applySuggestion = (suggestion) => {
    setForm((prev) => ({
      ...prev,
      title: prev.title || getSuggestionLabel(suggestion),
      description: prev.description || getSuggestionDescription(suggestion),
      priority: suggestion?.priority || prev.priority,
      taskType: suggestion?.task_type || prev.taskType,
    }));
  };

  const handleViewHistory = (task) => {
    if (!task?.id) {
      alert("Unable to open task history");
      return;
    }

    navigate(`/task-history/${task.id}`, {
      state: { taskTitle: task.title, taskSnapshot: task },
    });
  };

  const statusChartData = useMemo(
    () =>
      STATUS_TABS.filter((tab) => tab !== "All" && tab !== "Overdue").map((status) => ({
        name: status,
        value: tasks.filter((task) => normalizeStatus(task?.status) === status).length,
      })),
    [tasks]
  );

  const categoryChartData = useMemo(() => {
    const counts = new Map();
    tasks.forEach((task) => {
      const category = task?.category || "General";
      counts.set(category, (counts.get(category) || 0) + 1);
    });
    return Array.from(counts, ([name, value]) => ({ name, value })).slice(0, 8);
  }, [tasks]);

  const employeeChartData = useMemo(() => {
    const counts = new Map();
    tasks.forEach((task) => {
      const names = String(task?.assigned_employees || "Unassigned")
        .split(",")
        .map((name) => name.trim())
        .filter(Boolean);
      (names.length ? names : ["Unassigned"]).forEach((name) =>
        counts.set(name, (counts.get(name) || 0) + 1)
      );
    });
    return Array.from(counts, ([name, value]) => ({ name, value }))
      .sort((left, right) => right.value - left.value)
      .slice(0, 8);
  }, [tasks]);

  const trendChartData = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, index) => {
      const day = new Date();
      day.setDate(day.getDate() - (6 - index));
      return {
        key: day.toISOString().slice(0, 10),
        name: day.toLocaleDateString([], { month: "short", day: "numeric" }),
        completed: 0,
        overdue: 0,
      };
    });

    tasks.forEach((task) => {
      const createdKey = toDateKey(task?.updated_at) || toDateKey(task?.created_at);
      const bucket = days.find((day) => day.key === createdKey);
      if (bucket && isCompletedTask(task)) {
        bucket.completed += 1;
      }
    });

    days.forEach((day) => {
      day.overdue = tasks.filter((task) => {
        if (!isTaskOverdue(task) || !task?.deadline) return false;
        return toDateKey(task.deadline) <= day.key;
      }).length;
    });

    return days;
  }, [tasks]);

  const showAssignmentControls = true;
  const workspaceLocked = false;

  return (
    <div className="tasks-page">
      <section className="tasks-hero">
        <div>
          <p className="tasks-eyebrow">
            {isTl ? "TL Dashboard" : isEmployee ? "My Tasks" : "Task Control Center"}
          </p>
          <h1>Professional Task Workflow</h1>
          <p className="tasks-subtitle">
            Plan work, distribute responsibilities, and track delivery without
            disturbing the existing task APIs or current app users.
          </p>
        </div>
        <button type="button" className="ghost-action-btn" onClick={resetWorkspace}>
          {selectedTaskId ? "Start New Task" : "Reset Filters"}
        </button>
      </section>

      <section className="task-summary-grid">
        {summaryCards.map((card) => (
          <button
            key={card.label}
            type="button"
            className={`summary-card tone-${card.tone} ${
              filters.statusTab === card.filter ? "active" : ""
            }`}
           onClick={() =>
  setFilters((prev) => ({
    ...prev,
    status: "",
    statusTab: card.filter,
    viewMode:
      card.label === "Today's Followups"
        ? "todayFollowups"
        : card.label === "Today's Attachments"
        ? "todayAttachments"
        : "tasks",
  }))
}
          >
            <i
              className={`summary-bg-icon ${SUMMARY_CARD_ICON_CLASS[card.tone] || "fa fa-line-chart"}`}
              aria-hidden="true"
            ></i>
            <span>{card.label}</span>
            <strong>{card.value}</strong>
          </button>
        ))}
      </section>

      

      <section className="task-analytics-grid">
        <article className="analytics-panel">
          <h3>Tasks by Status</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={statusChartData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={82}>
                {statusChartData.map((entry, index) => (
                  <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </article>

        <article className="analytics-panel">
          <h3>Tasks by Category</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={categoryChartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill="#2563eb" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </article>

        <article className="analytics-panel">
          <h3>Tasks by Employee</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={employeeChartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill="#16a34a" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </article>

        <article className="analytics-panel analytics-panel-wide">
          <h3>Completion and Overdue Trend</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trendChartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="completed" stroke="#16a34a" fill="#bbf7d0" />
              <Area type="monotone" dataKey="overdue" stroke="#dc2626" fill="#fecaca" />
            </AreaChart>
          </ResponsiveContainer>
        </article>
      </section>

      <div className="tasks-shell">
        <aside className="task-workspace-card">
          <div className="workspace-header">
            <div>
              <h2>Create Task</h2>
              <p>
                {selectedTaskId
                  ? "Editing selected task. Save changes or clear to create a new one."
                  : "Create TL-based or direct employee assignments."}
              </p>
            </div>
            {selectedTaskId ? (
              <button type="button" className="text-action-btn" onClick={resetWorkspace}>
                Clear
              </button>
            ) : null}
          </div>

          <form onSubmit={handleSubmit} className="task-workspace-form">
      

            <label>
              <span>Task Title</span>
              <input
                value={form.title}
                onChange={(event) => handleFieldChange("title", event.target.value)}
                placeholder="Enter task title"
                required
                disabled={workspaceLocked}
              />
            </label>

            <label className="field-span-2">
              <span>Description</span>
              <textarea
                value={form.description}
                onChange={(event) =>
                  handleFieldChange("description", event.target.value)
                }
                placeholder="Add delivery details, expectations, or context"
                disabled={workspaceLocked}
              />
            </label>

            {showAssignmentControls ? (
              <label>
                <span>Assignment Type</span>
                <select
                  value={form.assignmentType}
                  onChange={(event) =>
                    handleFieldChange("assignmentType", event.target.value)
                  }
                  disabled={workspaceLocked}
                >
                  {ASSIGNMENT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {showAssignmentControls && form.assignmentType === "tl" ? (
              <div className="field-span-2">
                <span className="field-label-inline">Team Leader</span>
                <Select
                  options={tlOptions}
                  value={tlOptions.find(
                    (option) => Number(option.value) === Number(form.tlId)
                  )}
                  onChange={(selected) =>
                    handleFieldChange("tlId", selected?.value ? String(selected.value) : "")
                  }
                  placeholder="Select TL"
                  classNamePrefix="task-select"
                />
              </div>
            ) : null}

            {showAssignmentControls && form.assignmentType === "direct" ? (
              <div className="field-span-2">
                <span className="field-label-inline">Assign Employees</span>
                <Select
                  isMulti
                  options={employeeOptions}
                  value={employeeOptions.filter((option) =>
                    form.employeeIds.includes(option.value)
                  )}
                  onChange={(selected) =>
                    handleFieldChange(
                      "employeeIds",
                      (selected || []).map((option) => option.value)
                    )
                  }
                  placeholder="Select employees"
                  closeMenuOnSelect={false}
                  classNamePrefix="task-select"
                />
              </div>
            ) : null}

            <div className="task-category-field">
              <span className="field-label-inline">Category</span>
              <Select
                options={categoryOptions}
                value={selectedCategoryOption}
                onChange={(selected) =>
                  handleFieldChange("category", selected?.value || "")
                }
                placeholder="Search category..."
                className="task-category-select"
                classNamePrefix="task-select"
                isSearchable
                isLoading={categoriesLoading}
                isDisabled={workspaceLocked}
                isClearable
                noOptionsMessage={({ inputValue }) =>
                  inputValue ? "No matching categories" : "No categories available"
                }
              />
              <div className="task-category-meta">
                <small>
                  {categoriesLoading
                    ? "Loading categories..."
                    : "."}
                </small>
                {canManageCategories ? (
                  <button
                    type="button"
                    className="category-add-btn"
                    onClick={openCategoryModal}
                    disabled={workspaceLocked || categorySaving}
                  >
                    + Add New Category
                  </button>
                ) : null}
              </div>
              {categoriesError ? (
                <p className="field-feedback error-text">{categoriesError}</p>
              ) : null}
            </div>

            <label>
              <span>Priority</span>
              <select
                value={form.priority}
                onChange={(event) => handleFieldChange("priority", event.target.value)}
                disabled={workspaceLocked}
              >
                {PRIORITY_OPTIONS.map((priority) => (
                  <option key={priority} value={priority}>
                    {priority}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Deadline</span>
              <input
                type="date"
                value={form.deadline}
                onChange={(event) => handleFieldChange("deadline", event.target.value)}
                disabled={workspaceLocked}
              />
            </label>

            <label>
              <span>Task Type</span>
              <input
                value={form.taskType}
                onChange={(event) => handleFieldChange("taskType", event.target.value)}
                placeholder="Follow-up, Review, Dispatch..."
                disabled={workspaceLocked}
              />
            </label>

            <label>
              <span>Status</span>
              <select
                value={form.status}
                onChange={(event) => {
                  const nextStatus = event.target.value;
                  handleFieldChange("status", nextStatus);
                  if (nextStatus === "Completed") {
                    handleFieldChange("progress", 100);
                  }
                }}
                disabled={workspaceLocked}
              >
                {STATUS_OPTIONS.map((statusOption) => (
                  <option key={statusOption} value={statusOption}>
                    {statusOption}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Progress</span>
              <input
                type="number"
                min="0"
                max="100"
                value={form.progress}
                onChange={(event) =>
                  handleFieldChange("progress", Number(event.target.value) || 0)
                }
                disabled={workspaceLocked}
              />
            </label>

            <div className="field-span-2 suggestion-panel">
              <div className="suggestion-header">
                <span>Smart Suggestions</span>
                {suggestionsLoading ? <small>Loading...</small> : null}
              </div>
              {suggestions.length ? (
                <div className="suggestion-list">
                  {suggestions.map((suggestion) => (
                    <button
                      key={suggestion?.id || getSuggestionLabel(suggestion)}
                      type="button"
                      className="suggestion-chip"
                      onClick={() => applySuggestion(suggestion)}
                    >
                      {getSuggestionLabel(suggestion)}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="panel-empty-text">
                  {form.category
                    ? "No saved suggestions for this category yet."
                    : "Suggestions will appear when you enter a category."}
                </p>
              )}
            </div>

            <div className="field-span-2 workspace-actions">
              <button
                type="submit"
                className="primary-action-btn"
                disabled={saving || workspaceLocked}
              >
                {saving
                  ? "Saving..."
                  : workspaceLocked
                  ? "Select a Task First"
                  : selectedTaskId
                  ? "Update Task"
                  : "Assign Task"}
              </button>
              {selectedTaskId ? (
                <button
                  type="button"
                  className="secondary-action-btn"
                  onClick={() => handleViewHistory(selectedTask)}
                >
                  View Full History
                </button>
              ) : null}
            </div>
          </form>

          {selectedTaskId && canReassign ? (
            <div className="inline-panel">
              <div className="inline-panel-header">
                <h3>Reassignment</h3>
                <p>Distribute this task to one or multiple employees.</p>
              </div>
              <Select
                isMulti
                options={employeeOptions}
                value={employeeOptions.filter((option) =>
                  reassignEmployeeIds.includes(option.value)
                )}
                onChange={(selected) =>
                  setReassignEmployeeIds(
                    (selected || []).map((option) => option.value)
                  )
                }
                placeholder="Reassign to employees"
                closeMenuOnSelect={false}
                classNamePrefix="task-select"
              />
              <textarea
                value={reassignReason}
                onChange={(event) => setReassignReason(event.target.value)}
                placeholder="Why is this task being reassigned?"
              />
              <button
                type="button"
                className="secondary-action-btn full-width"
                onClick={handleReassign}
                disabled={reassigning}
              >
                {reassigning ? "Reassigning..." : "Save Reassignment"}
              </button>
            </div>
          ) : null}

          {selectedTaskId ? (
            <div className="inline-panel">
              <div className="inline-panel-header">
                <h3>Notes</h3>
                <p>Add working notes, blockers, or progress context.</p>
              </div>
              <textarea
                value={noteText}
                onChange={(event) => setNoteText(event.target.value)}
                placeholder="Add a quick note for this task"
              />
              <button
                type="button"
                className="secondary-action-btn full-width"
                onClick={handleAddNote}
                disabled={noteSaving}
              >
                {noteSaving ? "Saving Note..." : "Add Note"}
              </button>

              <div className="notes-stack">
                {notesLoading ? (
                  <p className="panel-empty-text">Loading notes...</p>
                ) : selectedTaskNotes.length ? (
                  selectedTaskNotes.map((note) => (
                    <article key={note.id || `${note.created_at}-${note.note}`} className="note-card">
                      <strong>{note?.employee_name || "Team Member"}</strong>
                      <p>{note?.note || "No note text"}</p>
                      <small>{formatDateTime(note?.created_at)}</small>
                    </article>
                  ))
                ) : (
                  <p className="panel-empty-text">No notes added yet.</p>
                )}
              </div>
            </div>
          ) : null}
        </aside>

        <section className="task-board-card">
          <div className="board-header">
            <div>
              <h2>{
                filters.viewMode === "todayFollowups" 
                  ? "Today's Followups" 
                  : filters.viewMode === "todayAttachments"
                  ? "Today's Attachments"
                  : "Task List"
              }</h2>
              <p>
                {
                  filters.viewMode === "todayFollowups" 
                    ? "Follow-ups scheduled for today." 
                    : filters.viewMode === "todayAttachments"
                    ? "Attachments uploaded today."
                    : "Use filters to switch between direct work, TL pipeline, and delivery status."
                }
              </p>
            </div>
            {filters.viewMode === "tasks" && (
              <div className="status-tabs" role="tablist" aria-label="Task status filters">
                {STATUS_TABS.map((status) => (
                  <button
                    key={status}
                    type="button"
                    className={filters.statusTab === status ? "active" : ""}
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, statusTab: status, status: "" }))
                    }
                  >
                    {status}
                  </button>
                ))}
              </div>
            )}
            {filters.viewMode === "tasks" && (
              <div className="board-filter-row">
                <input
                  value={filters.search}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, search: event.target.value }))
                  }
                  placeholder="Search task, category, assignee..."
                />
                <select
                  value={filters.status}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, status: event.target.value }))
                  }
                >
                  <option value="">All Status</option>
                  {STATUS_TABS.filter((statusOption) => !["All", "Overdue"].includes(statusOption)).map((statusOption) => (
                    <option key={statusOption} value={statusOption}>
                      {statusOption}
                    </option>
                  ))}
                </select>
                <select
                  value={filters.priority}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, priority: event.target.value }))
                  }
                >
                  <option value="">All Priority</option>
                  {PRIORITY_OPTIONS.map((priority) => (
                    <option key={priority} value={priority}>
                      {priority}
                    </option>
                  ))}
                </select>
                <select
                  value={filters.assignmentType}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      assignmentType: event.target.value,
                    }))
                  }
                >
                  <option value="">All Workflow</option>
                  {ASSIGNMENT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {loading ? (
            <div className="board-empty-state">Loading...</div>
          ) : filters.viewMode === "todayFollowups" ? (
            todayFollowups.length === 0 ? (
              <div className="board-empty-state">
                No follow-ups for today.
              </div>
            ) : (
              <div className="enterprise-task-list">
                {todayFollowups.map((followup) => {
                  const followupDateTime = formatFollowupDateTime(followup.followup_date, followup.followup_time);

                  return (
                    <article
                      key={followup.id}
                      className="enterprise-task-row"
                    >
                      <div className="task-row-main">
                        <div>
                          <div className="task-row-title">
                            <h3>{followup.task_title || "Untitled Task"}</h3>
                          </div>
                          <p>{followup.followup_note || "No follow-up note."}</p>
                        </div>
                      </div>
                      <div className="task-row-grid">
                        <span><strong>Employee</strong>{followup.employee_name || "Unknown"}</span>
                        <span><strong>Follow-up Date</strong>{followupDateTime.date}</span>
                        {followupDateTime.time ? (
                          <span><strong>Follow-up Time</strong>{followupDateTime.time}</span>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            )
          ) : filters.viewMode === "todayAttachments" ? (
            todayAttachments.length === 0 ? (
              <div className="board-empty-state">
                No attachments uploaded today.
              </div>
            ) : (
              <div className="enterprise-task-list">
                {todayAttachments.map((attachment) => {
                  // Build correct URLs using backend endpoints
                  const viewUrl = `${(import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || "https://techiohisab.com/api")}/tasks/attachments/${attachment.id}/view`;
                  const downloadUrl = `${(import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || "https://techiohisab.com/api")}/tasks/attachments/${attachment.id}/download`;
                  
                  return (
                <div
                  key={attachment.id}
                  className="enterprise-task-row"
                  style={{ cursor: "default" }}
                >
                  <div className="task-row-main" style={{ gap: '12px', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <div>
                      <div className="task-row-title">
                        <h3>{attachment.task_title || "Untitled Task"}</h3>
                      </div>
                    </div>
                    
                    {attachment.mime_type && attachment.mime_type.startsWith('image/') ? (
                      <img 
                        src={viewUrl} 
                        alt={attachment.original_name}
                        style={{
                          width: '120px',
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}
                      />
                    ) : (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <span style={{ fontSize: '36px' }}>📎</span>
                        <span>{attachment.original_name}</span>
                      </div>
                    )}

                    <div style={{ fontSize: '14px', color: '#4b5563', marginBottom: '8px' }}>
                      <strong>Employee:</strong> {attachment.employee_name || "Unknown"}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <a
                        href={viewUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="secondary-action-btn"
                        style={{ textDecoration: 'none', padding: '6px 16px' }}
                      >
                        View
                      </a>
                      <a
                        href={downloadUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="secondary-action-btn"
                        style={{ textDecoration: 'none', padding: '6px 16px' }}
                      >
                        Download
                      </a>
                    </div>
                  </div>
                </div>
                  );
                })}
              </div>
            )
          ) : filteredTasks.length === 0 ? (
            <div className="board-empty-state">
              No tasks matched the current filters.
            </div>
          ) : (
            <>
            
              <div className="enterprise-task-list">
                {filteredTasks.map((task) => (
                  <article
                    key={task.id}
                    className={`enterprise-task-row ${
                      Number(selectedTaskId) === Number(task.id) ? "active" : ""
                    } ${isTaskOverdue(task) ? "is-overdue" : ""}`}
                    onClick={() => openTask(task)}
                  >
                    <div className="task-row-main">
                      <div>
                        <div className="task-row-title">
                          <h3>{task.title}</h3>
                          {isTaskOverdue(task) ? <span className="overdue-pill">Overdue</span> : null}
                        </div>
                        <p>{task.description || "No description added."}</p>
                      </div>
                      <div className="task-row-meta">
                        <span className={getStatusClass(task.status)}>{normalizeStatus(task.status)}</span>
                        <span className={getPriorityClass(task.priority)}>{task.priority || "Low"}</span>
                      </div>
                    </div>

                    <div className="task-row-grid">
                      <span><strong>Category</strong>{task.category || "General"}</span>
                      <span><strong>Assignee</strong>{task.assigned_employees || "Unassigned"}</span>
                      <span><strong>Workflow</strong>{task.assignment_type === "tl" ? "TL Based" : "Direct"}</span>
                      <span><strong>Deadline</strong>{formatDateOnly(task.deadline)}</span>
                    </div>

                    <div className="task-row-footer">
                      <div className="progress-cell">
                        <strong>{Number(task.progress) || 0}%</strong>
                        <div className="progress-track">
                          <div
                            className="progress-fill"
                            style={{ width: `${Number(task.progress) || 0}%` }}
                          />
                        </div>
                      </div>
                      <div className="action-stack" onClick={(event) => event.stopPropagation()}>
                        <button type="button" onClick={() => openTask(task)}>
                          Open
                        </button>
                        <button type="button" onClick={() => handleViewHistory(task)}>
                          History
                        </button>
                        {canAssign && (
                          <button
                            type="button"
                            className="danger-btn"
                            onClick={() => handleDelete(task.id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </section>
      </div>

      {drawerOpen && selectedTask ? (
        <div className="task-drawer-backdrop" onClick={() => setDrawerOpen(false)}>
          <aside
            className="task-detail-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Task detail"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="drawer-header">
              <div>
                <span className="drawer-kicker">Task #{selectedTask.id}</span>
                <h2>{selectedTask.title}</h2>
                <div className="drawer-badges">
                  <span className={getStatusClass(selectedTask.status)}>
                    {normalizeStatus(selectedTask.status)}
                  </span>
                  <span className={getPriorityClass(selectedTask.priority)}>
                    {selectedTask.priority || "Low"}
                  </span>
                  {isTaskOverdue(selectedTask) ? <span className="overdue-pill">Overdue</span> : null}
                </div>
              </div>
              <button type="button" className="drawer-close-btn" onClick={() => setDrawerOpen(false)}>
                x
              </button>
            </div>

            <div className="drawer-tabs">
              {DRAWER_TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={drawerTab === tab ? "active" : ""}
                  onClick={() => setDrawerTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="drawer-body">
              {detailLoading ? <p className="panel-empty-text">Refreshing task details...</p> : null}

              {drawerTab === "Overview" ? (
                <div className="drawer-section-stack">
                  <section className="drawer-info-grid">
                    <span><strong>Description</strong>{selectedTask.description || "No description added."}</span>
                    <span><strong>Category</strong>{selectedTask.category || "General"}</span>
                    <span><strong>Task Type</strong>{selectedTask.task_type || "Task"}</span>
                    <span><strong>Assignment</strong>{selectedTask.assignment_type === "tl" ? "TL Based" : "Direct"}</span>
                    <span><strong>Team Leader</strong>{selectedTask.tl_name || "-"}</span>
                    <span><strong>Assignee</strong>{selectedTask.assigned_employees || "Unassigned"}</span>
                    <span><strong>Created</strong>{formatDateTime(selectedTask.created_at)}</span>
                    <span><strong>Deadline</strong>{formatDateOnly(selectedTask.deadline)}</span>
                  </section>
                  <div className="drawer-progress">
                    <div>
                      <strong>{Number(selectedTask.progress) || 0}%</strong>
                      <span>Progress</span>
                    </div>
                    <div className="progress-track">
                      <div
                        className="progress-fill"
                        style={{ width: `${Number(selectedTask.progress) || 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              ) : null}

              {drawerTab === "Timeline" ? (
                <div className="timeline-list">
                  {taskFlowLogs.length ? (
                    taskFlowLogs.map((log) => (
                      <article key={log.id || `${log.created_at}-${getTimelineLabel(log)}`} className="timeline-item">
                        <span className="timeline-dot" />
                        <div>
                          <strong>{getTimelineLabel(log)}</strong>
                          <p>{log.remarks || log.note || log.comment || log.message || log.log_message || "Task activity recorded."}</p>
                          <small>{formatDateTime(log.created_at)}</small>
                        </div>
                      </article>
                    ))
                  ) : (
                    <p className="panel-empty-text">No timeline events found.</p>
                  )}
                </div>
              ) : null}

              {drawerTab === "Notes" ? (
                <div className="drawer-section-stack">
                  <textarea
                    value={noteText}
                    onChange={(event) => setNoteText(event.target.value)}
                    placeholder="Add a quick note for this task"
                  />
                  <button type="button" className="secondary-action-btn" onClick={handleAddNote} disabled={noteSaving}>
                    {noteSaving ? "Saving Note..." : "Add Note"}
                  </button>
                  <div className="notes-stack">
                    {notesLoading ? (
                      <p className="panel-empty-text">Loading notes...</p>
                    ) : selectedTaskNotes.length ? (
                      selectedTaskNotes.map((note) => (
                        <article key={note.id || `${note.created_at}-${note.note}`} className="note-card">
                          <strong>{note?.employee_name || "Team Member"}</strong>
                          <p>{note?.note || "No note text"}</p>
                          <small>{formatDateTime(note?.created_at)}</small>
                        </article>
                      ))
                    ) : (
                      <p className="panel-empty-text">No notes added yet.</p>
                    )}
                  </div>
                </div>
              ) : null}

              {drawerTab === "Followups" ? (
                <div className="drawer-section-stack">
                  <div className="drawer-form-grid">
                    <label>
                      <span>Followup Date</span>
                      <input
                        type="datetime-local"
                        value={followupDate}
                        onChange={(event) => setFollowupDate(event.target.value)}
                      />
                    </label>
                    <label>
                      <span>Followup Remark</span>
                      <textarea
                        value={followupRemark}
                        onChange={(event) => setFollowupRemark(event.target.value)}
                        placeholder="Add followup remark"
                      />
                    </label>
                  </div>
                  <button type="button" className="secondary-action-btn" onClick={handleAddFollowup} disabled={followupSaving}>
                    {followupSaving ? "Saving Followup..." : "Add Followup"}
                  </button>
                  <div className="notes-stack">
                    {taskFollowups.length ? (
                      taskFollowups.map((followup) => {
                        const followupDateTime = formatFollowupDateTime(followup.followup_date, followup.followup_time);
                        const followupDateTimeLabel = followupDateTime.time
                          ? `${followupDateTime.date} • ${followupDateTime.time}`
                          : followupDateTime.date;

                        return (
                          <article key={followup.id} className="note-card">
                            <strong>{followupDateTimeLabel}</strong>
                            <p>{followup.remark}</p>
                            <small>
                              {followup.created_by_name || "Team Member"} • {formatDateTime(followup.created_at)}
                            </small>
                          </article>
                        );
                      })
                    ) : (
                      <p className="panel-empty-text">No followups added yet.</p>
                    )}
                  </div>
                </div>
              ) : null}

              {drawerTab === "Attachments" ? (
                <div className="drawer-section-stack">
                  <input
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                    onChange={(event) => setAttachmentFiles(Array.from(event.target.files || []))}
                  />
                  <button type="button" className="secondary-action-btn" onClick={handleUploadAttachments} disabled={attachmentSaving}>
                    {attachmentSaving ? "Uploading..." : "Upload Attachments"}
                  </button>
                  <div className="attachment-list">
                    {taskAttachments.length ? (
                      taskAttachments.map((attachment) => {
                        const viewUrl = `${(import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || "https://techiohisab.com/api")}/tasks/attachments/${attachment.id}/view`;
                        const downloadUrl = `${(import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || "https://techiohisab.com/api")}/tasks/attachments/${attachment.id}/download`;
                        return (
                        <div key={attachment.id} className="attachment-item" style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', borderBottom: '1px solid #eee' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <strong>{attachment.original_name}</strong>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <a
                                href={viewUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{ fontSize: '14px', textDecoration: 'none', color: '#2563eb' }}
                              >
                                View
                              </a>
                              <a
                                href={downloadUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{ fontSize: '14px', textDecoration: 'none', color: '#2563eb' }}
                              >
                                Download
                              </a>
                            </div>
                          </div>
                          <span style={{ fontSize: '12px', color: '#666' }}>{attachment.uploaded_by_name || "Uploaded"} • {formatDateTime(attachment.created_at)}</span>
                          {attachment.mime_type && attachment.mime_type.startsWith('image/') && (
                            <img 
                              src={viewUrl}
                              alt={attachment.original_name}
                              style={{
                                maxWidth: '200px',
                                maxHeight: '200px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '1px solid #eee'
                              }}
                            />
                          )}
                        </div>
                      );})
                    ) : (
                      <p className="panel-empty-text">No attachments uploaded yet.</p>
                    )}
                  </div>
                </div>
              ) : null}

              {drawerTab === "Reviews" ? (
                <div className="notes-stack">
                  {taskReviews.length ? (
                    taskReviews.map((review) => (
                      <article key={review.id || `${review.created_at}-${review.remark}`} className="note-card">
                        <strong>Rating: {review.rating ?? "-"}</strong>
                        <p>{review.remark || review.remarks || review.comment || "No review remark."}</p>
                        <small>{formatDateTime(review.created_at)}</small>
                      </article>
                    ))
                  ) : (
                    <p className="panel-empty-text">No reviews added yet.</p>
                  )}
                </div>
              ) : null}

              {drawerTab === "Employee Feedback" ? (
                <div className="notes-stack">
                  {taskFeedback.length ? (
                    taskFeedback.map((feedback) => (
                      <article key={feedback.id || `${feedback.created_at}-${feedback.feedback}`} className="note-card">
                        <strong>Rating: {feedback.rating ?? "-"}</strong>
                        <p>{feedback.feedback || "No feedback text."}</p>
                        <small>{formatDateTime(feedback.created_at)}</small>
                      </article>
                    ))
                  ) : (
                    <p className="panel-empty-text">No employee feedback submitted yet.</p>
                  )}
                </div>
              ) : null}
            </div>
          </aside>
        </div>
      ) : null}

      {isCategoryModalOpen ? (
        <div className="task-modal-backdrop" onClick={closeCategoryModal}>
          <div
            className="task-modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="task-category-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="inline-panel-header">
              <div>
                <h3 id="task-category-modal-title">Add Category</h3>
                <p>Save a new reusable task category without leaving this page.</p>
              </div>
              <button
                type="button"
                className="text-action-btn"
                onClick={closeCategoryModal}
                disabled={categorySaving}
              >
                Close
              </button>
            </div>

            <form onSubmit={handleCreateCategory} className="task-modal-form">
              <label>
                <span>Category Name</span>
                <input
                  value={newCategoryName}
                  onChange={(event) => setNewCategoryName(event.target.value)}
                  placeholder="Enter category name"
                  autoFocus
                  disabled={categorySaving}
                />
              </label>

              {categoryModalError ? (
                <p className="field-feedback error-text">{categoryModalError}</p>
              ) : (
                <p className="field-feedback">
                  New categories are saved to the database and become available immediately.
                </p>
              )}

              <div className="task-modal-actions">
                <button
                  type="button"
                  className="secondary-action-btn"
                  onClick={closeCategoryModal}
                  disabled={categorySaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-action-btn"
                  disabled={categorySaving}
                >
                  {categorySaving ? "Saving..." : "Save Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
